import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { AUTOPLAY_INTERVAL } from "@features/pulse/utils/constants";
import {
  PulseAutoPlayPaginationProps,
  SanitizedPulseData,
} from "@features/pulse/utils/types";
import { Switch } from "@headlessui/react";
import { cn } from "@util/helpers";
import { motion } from "motion/react";

type PulseDesktopAutoPlayPaginationProps = {
  /** Index of the active article card */
  activeArticle: number;
  /** List of articles to be displayed in the pagination */
  sortedPulseArticles: SanitizedPulseData[];
} & Pick<
  PulseAutoPlayPaginationProps,
  "autoplayTotal" | "isAutoplayEnabled" | "setIsAutoplayEnabled"
>;

/** Desktop Autoplay component for pulse
 * includes pagination and switch to enable/disable autoplay */
const PulseDesktopAutoPlayPagination = ({
  activeArticle,
  sortedPulseArticles,
  isAutoplayEnabled,
  setIsAutoplayEnabled,
  autoplayTotal,
}: PulseDesktopAutoPlayPaginationProps): React.ReactElement => {
  const { sendCustomEvent } = useSendPulseGaData();
  const handleAutoplayToggle = () => {
    sendCustomEvent(
      `desktop::auto play ${isAutoplayEnabled ? "off" : "on"}::${activeArticle}`,
      sortedPulseArticles[activeArticle - 1].title
    );

    setIsAutoplayEnabled(!isAutoplayEnabled);
  };

  return (
    <div
      className="absolute bottom-6 left-6 mx-auto flex items-center"
      data-testid="pulse-desktop-autoplay-pagination-component"
    >
      <Switch
        checked={isAutoplayEnabled}
        onChange={handleAutoplayToggle}
        className="group relative flex h-[15px] w-[26px] cursor-pointer rounded-full bg-white/15 p-[1px] ease-in-out aria-[checked=true]:bg-[#478CF6]"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none inline-block size-[13px] translate-x-0 rounded-full bg-white ring-0 transition duration-200 ease-in-out group-aria-[checked=true]:translate-x-[11px]"
        />
      </Switch>

      <span className="mx-3 inline-block font-poppins text-4xs tracking-2 text-white">
        Autoplay
      </span>

      {isAutoplayEnabled ? (
        <>
          {sortedPulseArticles.map((article, index) => {
            return (
              <motion.div
                key={`pulse-pagination-${article.id}`}
                animate={{
                  width: activeArticle === index + 1 ? "96px" : "8px",
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="mr-1 h-2 w-2 rounded-full bg-white/10"
              >
                <motion.div
                  className={cn(
                    "h-full rounded-full transition-colors duration-300",
                    {
                      "min-w-2 bg-white": index + 1 <= activeArticle,
                    }
                  )}
                  animate={{
                    width:
                      activeArticle === index + 1
                        ? `${(autoplayTotal / (AUTOPLAY_INTERVAL - 1000)) * 100}%`
                        : 0,
                  }}
                  transition={{
                    duration: activeArticle === index + 1 ? 1 : 0,
                    ease: "linear",
                  }}
                />
              </motion.div>
            );
          })}
        </>
      ) : null}
    </div>
  );
};

export default PulseDesktopAutoPlayPagination;
